/**
 * useCountryCompare · Module C 分国家对比数据 Hook
 *
 * 接口契约：specs/多游戏投放对比页/api/stats.md
 * URL：POST /api/v1/marketing/ad_creative_multi/stats
 *
 * 模式：`group_by=dimension_value` + `metric=country_stat`
 *   - dimensions[] 外层 = 国家（alpha-3）
 *   - values[] 内层 = 游戏 + value
 *   - 后端按 sort_by_dimension 决定游戏列顺序（所有国家行内一致）+ USA 置顶
 *
 * 设计要点：
 * - **请求方向**：sort_by_game_id 不再发后端（后端不支持），只能传 sort_by_dimension（按国家）
 *   → 当前 sort 模型仍是"按游戏排序游戏列"，由后端的 sort_by_dimension（默认 USA）决定列顺序
 *   → 用户改 sortBy 时只能本地反转 list（前端简单 reverse / 重排，不重发请求）
 * - **国家协议**：业务对外 country_code 仍是小写 alpha-2（与 cond / RegionCountryItem 对齐）
 *   接口 dimension_values 自动转 alpha-3 大写
 * - **响应映射**：row.country_code 取 display_country_code 转小写；games 来自 values 扁平
 *
 * countryCodes 协议（与 ChannelSelect 一致）：
 *   - `[]`              → 全选
 *   - `[UNSELECT_ALL]`  → 全不选
 *   - `[code, ...]`     → 部分选择（小写 alpha-2）
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { getStats } from '../services';
import { IFilter } from '../types/filter';
import { TSortOrder } from '../types/common';
import {
  ICountryRowItem,
  IStatsRawParams,
  IStatsRawResponse,
} from '../types/country';

export interface IUseCountryCompareArgs {
  filter: IFilter;
  /** 业务对外协议：小写 alpha-2 列表 */
  countryCodes: string[];
}

/**
 * 总计列特殊 sort key（与 game_id 共享 by_game_id 字段，用 sentinel 区分）
 *
 * 表格组件（CountryStackedTable / CountryTable）和 hook 共用这个常量，
 * 避免散落多处定义。
 */
export const TOTAL_SORT_KEY = '__total__';

/** 排序状态：仅"按某游戏的列方向" + asc/desc；只用于前端反转 list */
export interface ICountrySortByState {
  /** 按某游戏 ID 排序；特殊值 `TOTAL_SORT_KEY` 表示按行 total 排序 */
  by: string;
  order?: TSortOrder;
}

const EMPTY_RAW: IStatsRawResponse = {
  group_by: 'dimension_value',
  dimensions: [],
};

/** 序列化数组为稳定 key（顺序无关） */
function serializeIds(ids?: string[]): string {
  if (!ids || ids.length === 0) return '';
  return [...ids].sort().join(',');
}

/**
 * raw → ICountryRowItem[]
 * - country_code = display_country_code 转小写（alpha-2）
 * - games 直接从 values 扁平
 * - total 直接取后端返回
 * - link_value 累加到 old_total（前端简单累加，避免再发请求）
 */
function rawToList(raw: IStatsRawResponse): ICountryRowItem[] {
  return (raw.dimensions || []).map(g => {
    const games = (g.values || []).map(v => ({
      game_id: v.key,
      value: v.value || 0,
      old_value: v.link_value,
    }));
    const oldTotal = games.reduce((sum, x) => sum + (x.old_value ?? 0), 0);
    const code = (
      g.display_country_code ||
      g.dimension ||
      ''
    ).toLocaleLowerCase();
    return {
      country_code: code,
      games,
      total: g.total ?? games.reduce((s, x) => s + x.value, 0),
      old_total: oldTotal,
    };
  });
}

export function useCountryCompare({
  filter,
  countryCodes,
}: IUseCountryCompareArgs) {
  const [raw, setRaw] = useState<IStatsRawResponse>(EMPTY_RAW);
  const [sortBy, setSortBy] = useState<ICountrySortByState>({by: "", order: "desc"});
  const { wrapperCancelRequest, loading } = useCancelRequestHook();
  const countryIdsKey = serializeIds(countryCodes);

  /**
   * 请求参数：filter / countryCodes 变化时重发
   * - 不把 sortBy 发后端（接口的 sort_by_dimension 是"按国家"维度，与前端"按游戏列"sort 模型不同；
   *   保持后端默认 USA 作为列排序基准；用户 sort 仅前端反转）
   */
  const params = useMemo<IStatsRawParams | null>(() => {
    if (!filter?.start_time || !filter?.end_time || !filter?.game_ids?.length)
      return null;
    return {
      game_ids: filter.game_ids || [],
      start_time: filter.start_time,
      end_time: filter.end_time,
      date_type: filter.date_type,
      metric: 'country_stat',
      group_by: 'dimension_value',
      // 业务 alpha-2 → 接口 alpha-3
      dimension_values: countryCodes,
      need_link: true,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, countryIdsKey]);

  const request = useCallback(
    (nextParams: IStatsRawParams) => {
      wrapperCancelRequest(getStats, nextParams)
        .then((response: any) => {
          if (!response) return;
          setRaw({
            group_by: response.group_by || 'dimension_value',
            dimensions: response.dimensions || [],
            link_time: response.link_time,
          });
        })
        .catch(() => {});
    },
    [wrapperCancelRequest],
  );

  useEffect(() => {
    if (!params) {
      setRaw(EMPTY_RAW);
      return;
    }
    request(params);
  }, [params, request]);

  /** 转扁平结构 + 列顺序锁定（D8） */
  const { baseList, gameOrder } = useMemo(() => {
    const list = rawToList(raw);
    const order = list[0]?.games.map((g) => g.game_id) ?? [];
    return { baseList: list, gameOrder: order };
  }, [raw]);

 

  /**
   * 前端排序：
   * - 无排序（sortBy.by 为空）：美国置顶，其余保持接口原始顺序
   * - 有排序：全部行参与排序，美国不特殊处理
   * - 特殊 `TOTAL_SORT_KEY` → 按行 total 排序
   */
  const list = useMemo(() => { 
    if (!sortBy.by) {
      // 无排序字段：us 置顶，其余按 gameOrder[0] 的 value 降序排列
      const us = baseList.find(r => r.country_code === 'us');
      const firstGameId = gameOrder[0];
      const others = baseList.filter(r => r.country_code !== 'us');
      if (firstGameId) {
        others.sort((a, b) => {
          const av = a.games.find(g => g.game_id === firstGameId)?.value ?? 0;
          const bv = b.games.find(g => g.game_id === firstGameId)?.value ?? 0;
          return bv - av;
        });
      }
      return us ? [us, ...others] : others;
    }
    // 有排序字段：us 也参与排序
    const dir = sortBy.order === 'asc' ? 1 : -1;
    const isTotal = sortBy.by === TOTAL_SORT_KEY;
    const sorted = [...baseList].sort((a, b) => {
      const av = isTotal
        ? a.total ?? 0
        : a.games.find(g => g.game_id === sortBy.by)?.value ?? 0;
      const bv = isTotal
        ? b.total ?? 0
        : b.games.find(g => g.game_id === sortBy.by)?.value ?? 0;
      return (av - bv) * dir;
    });
    return sorted;
  }, [baseList, sortBy]);

  return {
    list,
    /** 列顺序锁定（D8）：取 list[0]（美国行）的 games 顺序 */
    gameOrder,
    /** 接口的环比时间窗口（透传） */
    linkTime: raw.link_time,
    sortBy,
    setSortBy,
    loading,
  };
}
